import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BellOff, BellRing, MailPlus, Mailbox } from 'lucide-react';
import {
  canInvitePerson, canRemindPerson, canResumeReminders, canStopReminders, entryPath, formatDay, nodeTitle,
  personReminders,
} from './programAdminModel';
import { OpenSpotTag, RoleText, StateTag } from './StateTag';
import {
  EmptyDay, MiniButton, PersonMeta, PersonName, ReminderCount, SpotLine, SpotList, StoppedTag, Table, TableScroll,
  Td, Th,
} from './programAdminStyles';

// The People area (plan §7.7 item 3): one row per person at this event - who they are, every spot
// they hold, when they were last invited, where the reminders stand, and the row actions. The actions
// are the backend's own: invite (one email covering the spots an invitation may start from), remind,
// stop and resume reminders (ReminderController; a reminder and a stop are per person and event, not
// per spot). Rows are built from the program and the person list (programAdminModel.peopleRows).
const PeopleArea = ({ rows = [], program, language, actions = {} }) => {
  const intl = useIntl();
  if (rows.length === 0) {
    return <EmptyDay><FormattedMessage id="programAdmin.people.empty" defaultMessage="Nobody is on this event's program yet." /></EmptyDay>;
  }
  return (
    <>
      <p style={{ margin: '0 0 .8rem', fontSize: '.82rem', color: '#64748b' }}>
        <FormattedMessage id="programAdmin.people.intro"
          defaultMessage="{count, plural, one {# person is} other {# people are}} on this event's program. A person may hold several spots."
          values={{ count: rows.length }} />
      </p>
      <TableScroll>
        <Table style={{ minWidth: '62rem' }}>
          <thead>
            <tr>
              <Th><FormattedMessage id="programAdmin.people.col.person" defaultMessage="Person" /></Th>
              <Th><FormattedMessage id="programAdmin.people.col.spots" defaultMessage="Spots" /></Th>
              <Th><FormattedMessage id="programAdmin.people.col.lastInvitation" defaultMessage="Last invitation" /></Th>
              <Th><FormattedMessage id="programAdmin.people.col.reminders" defaultMessage="Reminders" /></Th>
              <Th style={{ textAlign: 'right' }}><FormattedMessage id="programAdmin.people.col.actions" defaultMessage="Actions" /></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const reminders = personReminders(row);
              return (
                <tr key={row.personId}>
                  <Td>
                    <PersonName>{row.programName}</PersonName>
                    {row.institution && <PersonMeta>{row.institution}</PersonMeta>}
                    <PersonMeta>{row.contactEmail}</PersonMeta>
                  </Td>
                  <Td>
                    <SpotList>
                      {row.spots.map((spot) => (
                        <SpotLine key={spot.assignmentId}>
                          <RoleText role={spot.role} />
                          <span style={{ fontWeight: 600 }}>{nodeTitle(spot.entry, language)}</span>
                          <span style={{ color: '#64748b' }}>{entryPath(program, spot.entry, language)}</span>
                          <StateTag status={spot.status} />
                          {spot.openSpot && <OpenSpotTag />}
                        </SpotLine>
                      ))}
                    </SpotList>
                    {row.spots.length === 0 && (
                      <PersonMeta><FormattedMessage id="programAdmin.people.noSpot" defaultMessage="Not on the program (event body only)." /></PersonMeta>
                    )}
                  </Td>
                  <Td>{formatDay(intl, row.lastInvitedAt) || <FormattedMessage id="programAdmin.people.never" defaultMessage="Never invited" />}</Td>
                  <Td>
                    {reminders.openInvitations > 0 ? (
                      <>
                        {reminders.remindersSent != null && (
                          <ReminderCount $full={reminders.remindersSent >= reminders.maxReminders}>
                            <FormattedMessage id="programAdmin.people.remindersCount" defaultMessage="{sent} of {max}"
                              values={{ sent: reminders.remindersSent, max: reminders.maxReminders }} />
                          </ReminderCount>
                        )}
                        <PersonMeta>
                          <FormattedMessage id="programAdmin.people.openInvitations"
                            defaultMessage="{count, plural, one {# open invitation} other {# open invitations}}"
                            values={{ count: reminders.openInvitations }} />
                        </PersonMeta>
                        {reminders.remindersStopped && (
                          <div><StoppedTag><FormattedMessage id="programAdmin.people.stopped" defaultMessage="Stopped" /></StoppedTag></div>
                        )}
                      </>
                    ) : (
                      <PersonMeta><FormattedMessage id="programAdmin.people.nothingOpen" defaultMessage="Nothing open" /></PersonMeta>
                    )}
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <MiniButton type="button" disabled={!canInvitePerson(row)} onClick={() => actions.invite?.(row)}
                        title={canInvitePerson(row) ? undefined : intl.formatMessage({ id: 'programAdmin.people.inviteDisabled', defaultMessage: 'Needs at least one spot an invitation may start from (not a confirmed one).' })}
                        aria-label={intl.formatMessage({ id: 'programAdmin.people.inviteNamed', defaultMessage: 'Invite {name}' }, { name: row.programName })}>
                        <MailPlus size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.people.invite" defaultMessage="Invite" />
                      </MiniButton>
                      <MiniButton type="button" disabled={!canRemindPerson(row)} onClick={() => actions.remind?.(row)}
                        title={canRemindPerson(row) ? undefined : intl.formatMessage({ id: 'programAdmin.people.remindDisabled', defaultMessage: 'Nothing open to remind, or their reminders are stopped.' })}>
                        <BellRing size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.people.remind" defaultMessage="Remind" />
                      </MiniButton>
                      <MiniButton type="button" disabled={!canStopReminders(row)} onClick={() => actions.stopReminders?.(row)}
                        title={canStopReminders(row) ? undefined : intl.formatMessage({ id: 'programAdmin.people.stopDisabled', defaultMessage: 'Needs at least one open invitation whose reminders are not stopped.' })}>
                        <BellOff size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.people.stop" defaultMessage="Stop reminders" />
                      </MiniButton>
                      <MiniButton type="button" disabled={!canResumeReminders(row)} onClick={() => actions.resumeReminders?.(row)}
                        title={canResumeReminders(row) ? undefined : intl.formatMessage({ id: 'programAdmin.people.resumeDisabled', defaultMessage: 'Their reminders are running already.' })}>
                        <Mailbox size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.people.resume" defaultMessage="Resume reminders" />
                      </MiniButton>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableScroll>
    </>
  );
};

export default PeopleArea;
